import { makeStyles, tokens, Text, Avatar } from "@fluentui/react-components";
import type { Ticket } from "../api/tickets";
import StatusBadge from "./StatusBadge";
import PriorityBadge from "./PriorityBadge";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
  },
  item: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "flex-start",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
    flexWrap: "wrap",
  },
  timestamp: {
    flexShrink: 0,
  },
  empty: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXXL,
  },
});

function formatTimestamp(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffHours < 48) return "Yesterday";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

interface Props {
  tickets: Ticket[];
  onSelect: (ticket: Ticket) => void;
}

export default function TicketList({ tickets, onSelect }: Props) {
  const styles = useStyles();

  if (tickets.length === 0) {
    return (
      <div className={styles.empty}>
        <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>No tickets</Text>
      </div>
    );
  }

  return (
    <div className={styles.list}>
      {tickets.map((ticket) => (
        <div
          key={ticket.id}
          className={styles.item}
          onClick={() => onSelect(ticket)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && onSelect(ticket)}
        >
          <Avatar
            name={ticket.fromName ?? ticket.fromEmail ?? "?"}
            size={32}
          />
          <div className={styles.body}>
            <Text size={300} weight="semibold" truncate block>
              {ticket.subject}
            </Text>
            <div className={styles.meta}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>#{ticket.ticketNumber}</Text>
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              {ticket.assignedTo && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  {ticket.assignedTo.name}
                </Text>
              )}
            </div>
          </div>
          <div className={styles.timestamp}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {formatTimestamp(ticket.updatedAt)}
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
}
